import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Search,
  Edit3,
  Check,
  RefreshCw,
  Lock,
  Info,
  Loader2,
} from "lucide-react";
import { Supplier } from "../../types/supplier";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { STATE_NAME_MAP } from "../../lib/gst-rules";

interface AddSupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSupplierAdded: (supplier: Supplier) => void;
  companyTypes?: Array<{ id: number | string; name: string }>;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
}

/** PAN sits at characters 3–12 of a 15-char GSTIN (1-indexed). */
function extractPanFromGstin(value: string): string {
  const g = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (g.length < 12) return "";
  return g.slice(2, 12);
}

export function AddSupplierDialog({
  open,
  onOpenChange,
  onSupplierAdded,
  companyTypes = [],
}: AddSupplierDialogProps) {
  const [tabMode, setTabMode] = useState<"gstin" | "manual">("gstin");
  const [loading, setLoading] = useState(false);
  const [isFetchingGst, setIsFetchingGst] = useState(false);
  const [gstVerified, setGstVerified] = useState(false);

  // Form State
  const [gstin, setGstin] = useState("");
  const [entityType, setEntityType] = useState("");
  const [companyTypeId, setCompanyTypeId] = useState<string>("");
  const [pan, setPan] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [tradeName, setTradeName] = useState("");
  /** Probe / company-search id — sent as entity_id on add (GST + manual). */
  const [entityId, setEntityId] = useState("");

  // Manual: company name search (same API as Add Customer)
  const [entitySuggestions, setEntitySuggestions] = useState<
    Array<{ id: string; idType: string; name: string; status: string }>
  >([]);
  const [showEntitySuggestions, setShowEntitySuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Address State
  const [addressType, setAddressType] = useState("Registered Office");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  // SPOC State
  const [spocName, setSpocName] = useState("");
  const [spocMobile, setSpocMobile] = useState("");
  const [spocEmail, setSpocEmail] = useState("");

  const clearCompanySearch = () => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setShowEntitySuggestions(false);
    setEntitySuggestions([]);
    setIsSearching(false);
  };

  const resetForm = () => {
    setTabMode("gstin");
    setLoading(false);
    setIsFetchingGst(false);
    setGstVerified(false);
    setGstin("");
    setEntityType("");
    setCompanyTypeId("");
    setPan("");
    setCompanyName("");
    setTradeName("");
    setEntityId("");
    clearCompanySearch();
    setAddressType("Registered Office");
    setAddressLine1("");
    setCity("");
    setState("");
    setPincode("");
    setSpocName("");
    setSpocMobile("");
    setSpocEmail("");
  };

  // Clear all fields whenever dialog closes (cancel, overlay, or after submit)
  useEffect(() => {
    if (!open) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resolveEntityIdFromProbe = async (name: string): Promise<string> => {
    const query = name.trim();
    if (query.length <= 3) return "";
    try {
      const response = await apiService.post(`/customers/company/search`, { query });
      const list = ((response as { data?: any[] })?.data || []) as Array<{
        id?: string;
        name?: string;
      }>;
      if (!list.length) return "";
      const exact = list.find(
        (e) => (e.name || "").trim().toLowerCase() === query.toLowerCase()
      );
      return String(exact?.id || list[0]?.id || "");
    } catch {
      return "";
    }
  };

  const handleCompanyTypeChange = (value: string) => {
    setCompanyTypeId(value);
    const selected = companyTypes.find((t) => String(t.id) === String(value));
    setEntityType(selected?.name || "");
    setCompanyName("");
    setEntityId("");
    clearCompanySearch();
  };

  const handleCompanyNameSearch = (value: string) => {
    setCompanyName(value);
    // Typing clears prior selection until user picks from probe list
    setEntityId("");

    const typeNum = Number(companyTypeId);
    const skipCompanySearch = typeNum === 4 || typeNum === 5;

    if (value.trim().length > 3 && !skipCompanySearch) {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => {
        setIsSearching(true);
        apiService
          .post(`/customers/company/search`, { query: value })
          .then((response) => {
            setEntitySuggestions((response as { data: any[] }).data || []);
            setShowEntitySuggestions(true);
          })
          .catch(() => {
            setEntitySuggestions([]);
            setShowEntitySuggestions(false);
          })
          .finally(() => setIsSearching(false));
      }, 300);
    } else {
      clearCompanySearch();
    }
  };

  const handleFetchGstin = async () => {
    const cleanGstin = gstin.trim().toUpperCase();
    if (cleanGstin.length !== 15) {
      toast.error("Please enter a valid 15-character GSTIN");
      return;
    }

    setIsFetchingGst(true);
    try {
      const response: any = await apiService.post(`/supplier/fetch-supplier-gst`, {
        gstin: cleanGstin,
      });
      const supplier = response?.supplier;
      if (!supplier) {
        toast.error("No supplier details found for this GSTIN");
        return;
      }

      setGstin(supplier.gstin || cleanGstin);
      const fetchedName = supplier.companyName || "";
      setCompanyName(fetchedName);
      setTradeName(supplier.tradeName || "");
      setPan(supplier.pan || extractPanFromGstin(supplier.gstin || cleanGstin));

      const rawCompanyType =
        supplier.companyType != null ? String(supplier.companyType) : "";
      setCompanyTypeId(rawCompanyType);
      setEntityType(rawCompanyType);

      // Prefer entity_id from GST response; else resolve via probe company search
      const fromGst =
        supplier.entity_id ??
        supplier.entityId ??
        supplier.cin ??
        "";
      if (fromGst) {
        setEntityId(String(fromGst));
      } else if (fetchedName.trim()) {
        const resolved = await resolveEntityIdFromProbe(fetchedName);
        setEntityId(resolved);
      } else {
        setEntityId("");
      }

      const addr = supplier.address || {};
      setAddressType(
        addr.address_type === "registered_address" || !addr.address_type
          ? "Registered Office"
          : addr.address_type
      );
      setAddressLine1(addr.address_line1 || "");
      setCity(addr.city || "");
      setState(addr.state || "");
      setPincode(addr.pincode || "");

      if (supplier.primaryContactName) setSpocName(supplier.primaryContactName);
      if (supplier.primaryContactMobile) setSpocMobile(supplier.primaryContactMobile);
      if (supplier.primaryContactEmail) setSpocEmail(supplier.primaryContactEmail);

      setGstVerified(true);
      toast.success("GSTIN details fetched successfully");
    } catch {
      setGstVerified(false);
      toast.error("Failed to fetch GSTIN details");
    } finally {
      setIsFetchingGst(false);
    }
  };

  const handleGstinChange = (value: string) => {
    const upper = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    setGstin(upper);
    setGstVerified(false);
    setPan(extractPanFromGstin(upper));
    if (tabMode === "gstin") setEntityId("");
  };

  // Validation
  const missingFields: string[] = [];
  if (!spocName.trim()) missingFields.push("SPOC name");
  if (!spocMobile.trim()) missingFields.push("mobile");
  if (!spocEmail.trim()) missingFields.push("email");
  if (tabMode === "manual") {
    if (!companyTypeId) missingFields.push("company type");
    if (!companyName.trim()) missingFields.push("company name");
  } else if (!companyName.trim()) {
    missingFields.push("company name");
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (tabMode === "manual" && !companyTypeId) {
      toast.error("Please select a company type");
      return;
    }
    if (!companyName.trim()) {
      toast.error("Company Name is required");
      return;
    }
    if (!spocName.trim()) {
      toast.error("SPOC Name is required");
      return;
    }
    if (!spocMobile.trim() || !/^\d{10}$/.test(spocMobile.replace(/\D/g, "").slice(-10))) {
      toast.error("Mobile number must be a 10-digit number");
      return;
    }
    if (!spocEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(spocEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const resolvedPan = pan || extractPanFromGstin(gstin);

    setLoading(true);
    try {
      // Ensure entity_id is set (manual: from probe pick; GST: from fetch/resolve)
      let resolvedEntityId = entityId.trim();
      if (!resolvedEntityId && companyName.trim()) {
        resolvedEntityId = await resolveEntityIdFromProbe(companyName);
        if (resolvedEntityId) setEntityId(resolvedEntityId);
      }

      const supplierPayload = {
        companyName,
        tradeName: tradeName || companyName,
        primaryContactName: spocName,
        primaryContactMobile: spocMobile.replace(/\D/g, "").slice(-10),
        primaryContactEmail: spocEmail,
        companyType: companyTypeId || entityType,
        pan: resolvedPan,
        gstin,
        entity_id: resolvedEntityId || undefined,
        address: {
          address_type: addressType,
          address_line1: addressLine1,
          city,
          state,
          pincode,
          gstin,
        },
      };

      const response: any = await apiService.post<ApiResponse<any>>(`/supplier/add-supplier`, {
        supplier: supplierPayload,
      });

      if (response.status === 200 || response.data?.supplierId) {
        const newSupplier: Supplier = {
          id: response.data?.supplierId || Date.now(),
          company_name: companyName,
          trade_name: tradeName || companyName,
          pan: resolvedPan,
          spoc_details: [
            {
              id: response.data?.spocId || 1,
              name: spocName,
              email: spocEmail,
              mobile: spocMobile,
              is_primary: true,
            },
          ],
          addresses: [
            {
              id: "1",
              address_type: addressType,
              address_line1: addressLine1,
              city,
              state,
              pincode,
              gstin,
            },
          ],
          created_at: new Date().toISOString().split("T")[0],
          updated_at: new Date().toISOString().split("T")[0],
          po_count: 0,
          total_assets: 0,
          total_proforma_invoices: 0,
        };

        onSupplierAdded(newSupplier);
        toast.success("Supplier onboarded successfully!");
        onOpenChange(false);
      }
    } catch (err: any) {
      if (err?.status === 409) {
        toast.error("Supplier with this PAN/GSTIN already exists");
        return;
      }
      toast.error("Failed to add supplier. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (mode: "gstin" | "manual") => {
    setTabMode(mode);
    setGstVerified(false);
    setEntityId("");
    clearCompanySearch();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[960px] w-[95vw] max-h-[92vh] overflow-y-auto rounded-[14px] p-0 border border-[#dde3e4] bg-white shadow-2xl font-['Poppins',sans-serif] text-[#151b1d]">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-[#eaeeef] flex items-start justify-between">
          <div>
            <DialogTitle className="text-[17px] font-semibold text-[#151b1d]">
              Add New Supplier
            </DialogTitle>
            <p className="text-[12px] text-[#526066] mt-0.5">
              Enter the vendor details to initialize the onboarding process
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="relative px-6 py-4 space-y-5">
          {isFetchingGst && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[14px] bg-white/70">
              <div className="flex items-center gap-2 rounded-[8px] border border-[#dde3e4] bg-white px-3 py-2 text-xs font-medium text-[#151b1d] shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin text-[#3f8b98]" />
                Fetching GSTIN details…
              </div>
            </div>
          )}

          {/* Top Segmented Mode Selector */}
          <div className="space-y-2">
            <div className="bg-[#f6f8f8] border border-[#dde3e4] rounded-[9px] p-1 grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => switchTab("gstin")}
                className={`flex items-center justify-center gap-2 py-2 px-3 rounded-[7px] text-xs font-semibold transition-all cursor-pointer ${
                  tabMode === "gstin"
                    ? "bg-[#3f8b98] text-white shadow-sm"
                    : "text-[#526066] hover:text-[#151b1d]"
                }`}
              >
                <Search className="w-3.5 h-3.5" />
                <span>Onboard with GSTIN</span>
              </button>

              <button
                type="button"
                onClick={() => switchTab("manual")}
                className={`flex items-center justify-center gap-2 py-2 px-3 rounded-[7px] text-xs font-semibold transition-all cursor-pointer ${
                  tabMode === "manual"
                    ? "bg-[#3f8b98] text-white shadow-sm"
                    : "text-[#526066] hover:text-[#151b1d]"
                }`}
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Enter manually</span>
              </button>
            </div>

            <p className="text-[11.5px] text-[#526066] flex items-center gap-1.5 px-1">
              <Info className="w-3.5 h-3.5 text-[#3f8b98] flex-shrink-0" />
              <span>
                {tabMode === "gstin"
                  ? "We fetch the entity type, company name, PAN and address from the GST portal. You only add the contact person."
                  : "Select company type, search and pick the company name, then enter address and contact. PAN is taken from the GSTIN."}
              </span>
            </p>
          </div>

          {/* Section 1: Verify GSTIN — GSTIN onboard only */}
          {tabMode === "gstin" && (
            <div className="space-y-2 border border-[#dde3e4] rounded-[10px] p-4 bg-white">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#e2f2e8] text-[#227a4c] flex items-center justify-center text-xs font-bold">
                  <Check className="w-3 h-3 stroke-[2.5]" />
                </span>
                <span className="text-[13px] font-semibold text-[#151b1d]">
                  Verify GSTIN
                </span>
              </div>

              <div className="space-y-2 pt-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  GSTIN <span className="text-[#b0453f]">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      maxLength={15}
                      placeholder="e.g. 29ABBCA4070B1Z3"
                      value={gstin}
                      onChange={(e) => handleGstinChange(e.target.value)}
                      className="w-full h-9 pl-3.5 pr-9 rounded-[7px] border border-[#dde3e4] bg-white text-[13px] font-medium text-[#151b1d] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none uppercase"
                    />
                    {gstVerified && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#e2f2e8] text-[#227a4c] flex items-center justify-center">
                        <Check className="w-3 h-3 stroke-[2.5]" />
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => void handleFetchGstin()}
                    disabled={isFetchingGst || gstin.trim().length !== 15}
                    className="h-9 px-3.5 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isFetchingGst ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#3f8b98]" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5 text-[#526066]" />
                    )}
                    <span>{gstVerified ? "Fetch again" : "Fetch"}</span>
                  </button>
                </div>

                {gstVerified && (
                  <div className="bg-[#e2f2e8] border border-[#bfe9cf] text-[#227a4c] rounded-[7px] px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    <span>GSTIN verified. Status Active · Regular taxpayer</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 2: Company Details */}
          <div className="space-y-3 border border-[#dde3e4] rounded-[10px] p-4 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#e2f2e8] text-[#227a4c] flex items-center justify-center text-xs font-bold">
                  <Check className="w-3 h-3 stroke-[2.5]" />
                </span>
                <span className="text-[13px] font-semibold text-[#151b1d]">
                  {tabMode === "manual" ? "Company details" : "Check company details"}
                </span>
              </div>
              {tabMode === "gstin" && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#e2f2e8] text-[#227a4c] text-[10.5px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1e9a52]" />
                  Auto-filled from GST portal
                </span>
              )}
            </div>

            {tabMode === "manual" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {/* Company Type first */}
                <div className="space-y-1">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                    Company Type <span className="text-[#b0453f]">*</span>
                  </label>
                  <Select value={companyTypeId || ""} onValueChange={handleCompanyTypeChange}>
                    <SelectTrigger className="h-9 rounded-[7px] border-[#dde3e4] text-[12.5px] font-medium text-[#151b1d] focus:ring-1 focus:ring-[#3f8b98]">
                      <SelectValue placeholder="Select company type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-60 overflow-y-auto z-[500]">
                      {companyTypes.map((type) => (
                        <SelectItem key={String(type.id)} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Company Name — after type selected */}
                <div className="space-y-1">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                    Company Name <span className="text-[#b0453f]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={companyName}
                      disabled={!companyTypeId}
                      onChange={(e) => handleCompanyNameSearch(e.target.value)}
                      placeholder={
                        companyTypeId
                          ? "Type to search and select company"
                          : "Select company type first"
                      }
                      className={`w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] text-[12.5px] font-medium text-[#151b1d] outline-none ${
                        companyTypeId
                          ? "bg-white focus:border-[#3f8b98]"
                          : "bg-[#f6f8f8] cursor-not-allowed text-[#7c8b91]"
                      }`}
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#3f8b98]" />
                      </div>
                    )}
                    {showEntitySuggestions && entitySuggestions.length > 0 && (
                      <div className="absolute z-[500] w-full mt-1 bg-white border border-[#dde3e4] rounded-[7px] shadow-lg max-h-60 overflow-y-auto">
                        {entitySuggestions.map((entity, index) => (
                          <button
                            key={`${entity.id}-${index}`}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-[#f6f8f8] cursor-pointer flex items-center gap-2 text-xs"
                            onClick={() => {
                              setCompanyName(entity.name);
                              setEntityId(String(entity.id || ""));
                              setShowEntitySuggestions(false);
                              setEntitySuggestions([]);
                            }}
                          >
                            <span
                              className={`w-2 h-2 rounded-full shrink-0 ${
                                entity.status === "ACTIVE" ? "bg-green-500" : "bg-red-500"
                              }`}
                            />
                            <span className="truncate">{entity.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showEntitySuggestions &&
                      entitySuggestions.length === 0 &&
                      companyName.trim().length > 3 &&
                      !isSearching && (
                        <div className="absolute z-[500] w-full mt-1 bg-white border border-[#dde3e4] rounded-[7px] shadow-lg p-3 text-xs text-[#526066]">
                          No companies found
                        </div>
                      )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="space-y-1">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                    Entity Type <span className="text-[#b0453f]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={entityType}
                      readOnly
                      className="w-full h-9 pl-3 pr-8 rounded-[7px] border border-[#dde3e4] text-[12.5px] font-medium text-[#151b1d] outline-none bg-[#f6f8f8] cursor-not-allowed"
                    />
                    <Lock className="w-3.5 h-3.5 text-[#7c8b91] absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                    Company Name <span className="text-[#b0453f]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={companyName}
                      readOnly
                      className="w-full h-9 pl-3 pr-8 rounded-[7px] border border-[#dde3e4] text-[12.5px] font-medium text-[#151b1d] uppercase truncate outline-none bg-[#f6f8f8] cursor-not-allowed"
                    />
                    <Lock className="w-3.5 h-3.5 text-[#7c8b91] absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
              </div>
            )}

            {/* Trade Name — GSTIN onboard only */}
            {tabMode === "gstin" && (
              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  Trade Name
                </label>
                <input
                  type="text"
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                  placeholder="Trade Name (optional)"
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                />
              </div>
            )}
          </div>

          {/* Section 3: Registered Address */}
          <div className="space-y-3 border border-[#dde3e4] rounded-[10px] p-4 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#e2f2e8] text-[#227a4c] flex items-center justify-center text-xs font-bold">
                  <Check className="w-3 h-3 stroke-[2.5]" />
                </span>
                <span className="text-[13px] font-semibold text-[#151b1d]">
                  {tabMode === "manual" ? "Registered address" : "Check registered address"}
                </span>
              </div>
              {tabMode === "gstin" && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#e2f2e8] text-[#227a4c] text-[10.5px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1e9a52]" />
                  Auto-filled from GST portal
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <div className="space-y-1 sm:col-span-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  Address Type <span className="text-[#b0453f]">*</span>
                </label>
                <select
                  value={addressType}
                  onChange={(e) => setAddressType(e.target.value)}
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none cursor-pointer"
                >
                  <option value="Registered Office">Registered Office</option>
                  <option value="Branch">Branch Office</option>
                  <option value="Warehouse">Warehouse</option>
                </select>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  Address Line 1 <span className="text-[#b0453f]">*</span>
                </label>
                <input
                  type="text"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Street address, building, suite"
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  City <span className="text-[#b0453f]">*</span>
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  State <span className="text-[#b0453f]">*</span>
                </label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none cursor-pointer"
                >
                  <option value="">Select state</option>
                  {Object.values(STATE_NAME_MAP).map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  Pincode <span className="text-[#b0453f]">*</span>
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit PIN"
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  GSTIN <span className="text-[#b0453f]">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    maxLength={15}
                    value={gstin}
                    readOnly={tabMode === "gstin"}
                    onChange={(e) => handleGstinChange(e.target.value)}
                    placeholder="15-char GSTIN"
                    className={`w-full h-9 pl-3 pr-7 rounded-[7px] border border-[#dde3e4] text-[12px] font-medium text-[#151b1d] uppercase outline-none ${
                      tabMode === "gstin"
                        ? "bg-[#f6f8f8] cursor-not-allowed"
                        : "bg-white focus:border-[#3f8b98]"
                    }`}
                  />
                  {tabMode === "gstin" && (
                    <Lock className="w-3.5 h-3.5 text-[#7c8b91] absolute right-2 top-1/2 -translate-y-1/2" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Contact Information */}
          <div className="space-y-3 border border-[#dde3e4] rounded-[10px] p-4 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#3f8b98] text-white flex items-center justify-center text-xs font-bold">
                  4
                </span>
                <span className="text-[13px] font-semibold text-[#151b1d]">
                  Add contact information
                </span>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold ${
                  missingFields.length > 0
                    ? "bg-[#f3e7c9] text-[#8c6500]"
                    : "bg-[#e2f2e8] text-[#227a4c]"
                }`}
              >
                {missingFields.length > 0
                  ? `● ${missingFields.length} fields to fill`
                  : "● All fields filled"}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  SPOC Name <span className="text-[#b0453f]">*</span>
                </label>
                <input
                  type="text"
                  value={spocName}
                  onChange={(e) => setSpocName(e.target.value)}
                  placeholder="Full name"
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  Mobile <span className="text-[#b0453f]">*</span>
                </label>
                <div className="flex items-center">
                  <span className="h-9 px-2.5 bg-[#f6f8f8] border border-r-0 border-[#dde3e4] rounded-l-[7px] text-[12px] font-medium text-[#526066] flex items-center">
                    +91
                  </span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={spocMobile}
                    onChange={(e) => setSpocMobile(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile"
                    className="w-full h-9 px-3 rounded-r-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                  Email Address <span className="text-[#b0453f]">*</span>
                </label>
                <input
                  type="email"
                  value={spocEmail}
                  onChange={(e) => setSpocEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full h-9 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[12.5px] font-medium text-[#151b1d] focus:border-[#3f8b98] outline-none"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-[#eaeeef]">
            <div className="text-[11.5px] text-[#8c6500] font-medium">
              {missingFields.length > 0 ? (
                <span>
                  ⓘ {missingFields.length} required field{missingFields.length > 1 ? "s" : ""} left:{" "}
                  {missingFields.join(", ")}
                </span>
              ) : (
                <span className="text-[#227a4c]">✓ Ready to submit</span>
              )}
            </div>

            <div className="flex items-center gap-2.5 justify-end">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-9 px-4 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium transition-colors cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="h-9 px-5 rounded-[7px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50"
              >
                {loading ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
